import { createInertiaApp, router } from '@inertiajs/react'
import React from "react"

import { wrapInertiaApp } from "@/entrypoints/inertia_app"
import { trackPageView } from "@/lib/analytics"
import { preparePosthogHistoryStep, preparePosthogPage, syncPosthogPage } from "@/lib/posthog"
import '../styles/application.css'
import type { SharedProps } from "@/types"

const BUGSNAG_API_KEY = import.meta.env.VITE_BUGSNAG_API_KEY?.trim()
const BUGSNAG_RELEASE_STAGE = import.meta.env.VITE_BUGSNAG_RELEASE_STAGE?.trim() || import.meta.env.MODE
const BUGSNAG_ENABLED_RELEASE_STAGES = (import.meta.env.VITE_BUGSNAG_ENABLED_RELEASE_STAGES || "production,staging")
  .split(",")
  .map((stage) => stage.trim())
  .filter(Boolean)
const BUGSNAG_APP_VERSION = import.meta.env.VITE_APP_VERSION?.trim()
const BUGSNAG_APP_VERSION_CONFIG = BUGSNAG_APP_VERSION ? { appVersion: BUGSNAG_APP_VERSION } : {}
const bugsnagEnabled = Boolean(BUGSNAG_API_KEY) && BUGSNAG_ENABLED_RELEASE_STAGES.includes(BUGSNAG_RELEASE_STAGE)
const isBrowser = typeof window !== "undefined"

type CurrentUser = {
  email_address: string
  id: string
} | null | undefined

type BugsnagClient = typeof import("@bugsnag/js")["default"]

let bugsnagClient: BugsnagClient | undefined
let bugsnagPromise: Promise<BugsnagClient | undefined> | undefined
let currentUser: CurrentUser

declare global {
  interface Window {
    __superBaziBugsnagStarted?: boolean
    __superBaziBugsnagScheduled?: boolean
    __superBaziInertiaBootstrapped?: boolean
  }
}

function startBugsnag() {
  if (!isBrowser || !BUGSNAG_API_KEY || !bugsnagEnabled) return Promise.resolve(undefined)
  if (bugsnagPromise) return bugsnagPromise

  bugsnagPromise = Promise.all([
    import("@bugsnag/js"),
    import("@bugsnag/browser-performance"),
  ]).then(([bugsnagModule, performanceModule]) => {
    const Bugsnag = bugsnagModule.default

    if (!window.__superBaziBugsnagStarted) {
      window.__superBaziBugsnagStarted = true
      Bugsnag.start({
        apiKey: BUGSNAG_API_KEY,
        enabledReleaseStages: BUGSNAG_ENABLED_RELEASE_STAGES,
        releaseStage: BUGSNAG_RELEASE_STAGE,
        ...BUGSNAG_APP_VERSION_CONFIG,
      })
      performanceModule.default.start({
        apiKey: BUGSNAG_API_KEY,
        enabledReleaseStages: BUGSNAG_ENABLED_RELEASE_STAGES,
        releaseStage: BUGSNAG_RELEASE_STAGE,
        ...BUGSNAG_APP_VERSION_CONFIG,
      })
    }

    bugsnagClient = Bugsnag
    Bugsnag.setUser(currentUser?.id ?? null, currentUser?.email_address ?? null)

    return Bugsnag
  })

  return bugsnagPromise
}

function scheduleBugsnag(user: CurrentUser) {
  if (!isBrowser || !bugsnagEnabled) return

  currentUser = user
  if (bugsnagClient) {
    bugsnagClient.setUser(user?.id ?? null, user?.email_address ?? null)
    return
  }
  if (window.__superBaziBugsnagScheduled) return

  window.__superBaziBugsnagScheduled = true
  const initialize = () => { void startBugsnag() }

  if (document.readyState === "complete") {
    initialize()
  } else {
    window.addEventListener("load", initialize, { once: true })
  }
}

class DeferredBugsnagBoundary extends React.Component<{
  children: React.ReactNode
  user: CurrentUser
}, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    void startBugsnag().then((Bugsnag) => {
      Bugsnag?.notify(error, (event) => {
        event.setUser(this.props.user?.id, this.props.user?.email_address)
        event.addMetadata("react", { componentStack: info.componentStack })
      })
    })
  }

  render() {
    return this.state.hasError ? null : this.props.children
  }
}

let posthogNavigationBound = false

if (isBrowser && !window.__superBaziInertiaBootstrapped) {
  window.__superBaziInertiaBootstrapped = true

  void createInertiaApp<SharedProps>({
    pages: "../pages",
    strictMode: true,
    title: (title) => {
      // Inertia invokes this while applying <Head>. Read the browser title
      // on the next frame, after the head update has finished.
      if (isBrowser) requestAnimationFrame(() => trackPageView(window.location.href))
      return title
    },

    withApp(app, { page, ssr }) {
      const user = page.props.auth?.user
      scheduleBugsnag(user)
      if (!ssr && !posthogNavigationBound) {
        posthogNavigationBound = true
        syncPosthogPage(page.props)
        router.on("beforeUpdate", (event) => preparePosthogPage(event.detail.page.props as SharedProps))
        window.addEventListener("popstate", preparePosthogHistoryStep)
        router.on("navigate", (event) => syncPosthogPage(event.detail.page.props as SharedProps))
      }

      const appWithProviders = wrapInertiaApp(app)

      if (ssr || !bugsnagEnabled) return appWithProviders

      return (
        <DeferredBugsnagBoundary user={user}>
          {appWithProviders}
        </DeferredBugsnagBoundary>
      )
    },

    defaults: {
      form: {
        forceIndicesArrayFormatInFormData: false,
        withAllErrors: true,
      },
      visitOptions: () => {
        return { queryStringArrayFormat: "brackets" }
      },
    },
  }).catch((error) => {
    // This ensures this entrypoint is only loaded on Inertia pages
    // by checking for the presence of the root element (#app by default).
    // Feel free to remove this `catch` if you don't need it.
    if (document.getElementById("app")) {
      throw error
    } else {
      console.error(
        "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
      )
    }
  })
}
